import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { User, Lock, Check } from 'lucide-react';

const PROFILE_TYPES = [
  { value: 'artist', label: 'Artist' },
  { value: 'collector', label: 'Collector' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'dealer', label: 'Dealer' },
  { value: 'advisor', label: 'Art Advisor' },
];

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'security', label: 'Security', icon: Lock },
];

export default function MemberProfile() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState('profile');

  const [form, setForm] = useState({
    name: user?.name || '',
    profile_type: user?.profile_type || '',
    bio: user?.bio || '',
    location: user?.location || '',
    website: user?.website || '',
    phone: user?.phone || '',
    portfolio_url: user?.portfolio_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPw, setChangingPw] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/profile', form);
      await refreshUser();
      setSaved(true);
      toast.success('Profile updated');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (!pw.current_password) { toast.error('Enter your current password'); return; }
    if (pw.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error('Passwords do not match'); return; }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: pw.current_password, new_password: pw.new_password });
      toast.success('Password changed successfully');
      setPw({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPw(false); }
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <DashboardShell title="Account Settings">
      <div className="max-w-2xl space-y-6">

        {/* Identity card */}
        <div className="flex items-center gap-4 p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            {initials}
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate" style={{ color: 'var(--text)', fontFamily: 'var(--font-serif)' }}>{user?.name}</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
            <span
              className="inline-block mt-1 text-xs px-2.5 py-0.5 rounded-full capitalize font-medium"
              style={{ backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)' }}
            >
              {user?.membership_status === 'approved' ? 'Approved Member' : user?.role}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ backgroundColor: 'var(--bg-subtle)' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: active ? 'var(--surface)' : 'transparent',
                  color: active ? 'var(--text)' : 'var(--text-muted)',
                  boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}
                data-testid={`settings-tab-${t.id}`}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div className="p-6 rounded-xl border space-y-5" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
            <form onSubmit={handleProfileSave} className="space-y-5">

              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Full Name *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Your full name"
                  style={{ borderColor: 'var(--border-clr)' }}
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <Label className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-muted)' }}>I am a</Label>
                <div className="flex flex-wrap gap-2">
                  {PROFILE_TYPES.map(r => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, profile_type: r.value }))}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                      style={{
                        borderColor: form.profile_type === r.value ? 'var(--text)' : 'var(--border-clr)',
                        backgroundColor: form.profile_type === r.value ? 'var(--text)' : 'transparent',
                        color: form.profile_type === r.value ? 'var(--bg)' : 'var(--text-muted)',
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Bio</Label>
                <Textarea
                  value={form.bio}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  rows={3}
                  placeholder="A short description about you or your practice…"
                  style={{ borderColor: 'var(--border-clr)' }}
                  data-testid="profile-bio-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: 'location', label: 'Location', placeholder: 'City, Country' },
                  { key: 'phone', label: 'Phone', placeholder: '+44 7700 000000' },
                  { key: 'website', label: 'Website', placeholder: 'https://yoursite.com' },
                  { key: 'portfolio_url', label: 'Portfolio URL', placeholder: 'https://portfolio.com' },
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>{label}</Label>
                    <Input
                      value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={{ borderColor: 'var(--border-clr)' }}
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-end pt-2 border-t" style={{ borderColor: 'var(--border-clr)' }}>
                <Button
                  type="submit"
                  disabled={saving}
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px', minWidth: '130px' }}
                  data-testid="save-profile-btn"
                >
                  {saving ? 'Saving…' : saved ? <><Check size={14} className="mr-1.5 inline" />Saved</> : 'Save Profile'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Security Tab */}
        {tab === 'security' && (
          <div className="p-6 rounded-xl border space-y-5" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text)' }}>Change Password</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Use a strong password of at least 8 characters.</p>
            </div>
            <form onSubmit={handleChangePw} className="space-y-4">
              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Current Password</Label>
                <Input
                  type="password"
                  value={pw.current_password}
                  onChange={e => setPw(p => ({ ...p, current_password: e.target.value }))}
                  placeholder="Your current password"
                  style={{ borderColor: 'var(--border-clr)' }}
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>New Password</Label>
                <Input
                  type="password"
                  value={pw.new_password}
                  onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))}
                  placeholder="Min. 8 characters"
                  style={{ borderColor: 'var(--border-clr)' }}
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-muted)' }}>Confirm New Password</Label>
                <Input
                  type="password"
                  value={pw.confirm_password}
                  onChange={e => setPw(p => ({ ...p, confirm_password: e.target.value }))}
                  placeholder="Repeat new password"
                  style={{
                    borderColor: pw.confirm_password && pw.new_password !== pw.confirm_password
                      ? '#EF4444' : 'var(--border-clr)'
                  }}
                  data-testid="confirm-password-input"
                />
                {pw.confirm_password && pw.new_password !== pw.confirm_password && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>Passwords do not match</p>
                )}
              </div>

              <div className="flex justify-end pt-2 border-t" style={{ borderColor: 'var(--border-clr)' }}>
                <Button
                  type="submit"
                  disabled={changingPw}
                  variant="outline"
                  style={{ borderColor: 'var(--border-strong)', borderRadius: '10px', minWidth: '150px' }}
                  data-testid="change-password-btn"
                >
                  {changingPw ? 'Updating…' : 'Update Password'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
